import gsap from 'gsap';
import AbstractComponent from './abstract-component';

function getFxLayer() {
  let fx = document.getElementById('fx-layer');
  if (!fx) {
    fx = document.createElement('div');
    fx.id = 'fx-layer';
    Object.assign(fx.style, {
      position: 'fixed',
      left: '0', top: '0',
      width: '100vw', height: '100vh',
      pointerEvents: 'none',
      zIndex: '2147483647',
      contain: 'layout style paint',
    });
    document.body.appendChild(fx);
  }
  return fx;
}

export default class CursorHalo extends AbstractComponent {
  constructor(el) {
    super(el);
    this.DOM = {};
    this._enabled = matchMedia('(pointer:fine)').matches;
    this._rect = null;
    this._handlers = {};
    this._useViewport = false;
    this._hostIsSvg = false;
    this.$area = null;
    this.initDom();
    this.initStyles();
    this.initMotion();
    this.initEvents();
  }

  initDom() {
    const ds = this.$el.dataset;
    this._hostIsSvg = this.$el instanceof SVGElement;
    this._useViewport = this._hostIsSvg || (ds.haloAttach || '').toLowerCase() === 'fixed';

    const sel = (ds.haloArea || '').trim();
    if (sel) {
      this.$area = document.querySelector(sel);
    }
    if (!this.$area) {
      const svg = this._hostIsSvg ? this.$el.ownerSVGElement : null;
      this.$area = svg || this.$el;
    }

    const halo = document.createElement('div');
    const inner = document.createElement('div');
    halo.className = 'cursor-halo';
    inner.className = 'cursor-halo__inner';
    halo.appendChild(inner);

    if (this._useViewport) {
      getFxLayer().appendChild(halo);
    } else {
      const cs = getComputedStyle(this.$area);
      if (cs.position === 'static') this.$area.style.position = 'relative';
      this.$area.appendChild(halo);
    }

    this.DOM.halo = halo;
    this.DOM.inner = inner;

    this.opts = {
      w: ds.haloWidth || '35vh',
      h: ds.haloHeight || '25vh',
      rot: parseFloat(ds.haloRotate || '-25.799'),
      blur: ds.haloBlur || '100px',
      opacity: parseFloat(ds.haloOpacity || '0.5'),
      gradient: ds.haloGradient || 'linear-gradient(90deg,#01A981 5.81%,#152A2A 98.35%)',
      ease: ds.haloEase || 'power3.out',
      damp: parseFloat(ds.haloDamp || '0.18'),
      offX: parseFloat(ds.haloOffsetX || '0'),
      offY: parseFloat(ds.haloOffsetY || '0'),
    };
  }

  _recalcRect = () => {
    if (!this._useViewport) this._rect = this.$area.getBoundingClientRect();
  };

  initStyles() {
    Object.assign(this.DOM.halo.style, {
      position: this._useViewport ? 'fixed' : 'absolute',
      left: '0', top: '0',
      width: this.opts.w, height: this.opts.h,
      pointerEvents: 'none',
      opacity: '0',
      willChange: 'transform, opacity',
      transformOrigin: '50% 50%',
    });

    Object.assign(this.DOM.inner.style, {
      width: '100%', height: '100%',
      transform: `rotate(${this.opts.rot}deg)`,
      borderRadius: '390.086px',
      background: this.opts.gradient,
      filter: `blur(${this.opts.blur})`,
      opacity: '1',
      pointerEvents: 'none',
      transformOrigin: '50% 50%',
    });
  }

  initMotion() {
    gsap.set(this.DOM.halo, { xPercent: -50, yPercent: -50, force3D: true });
    this._qToX = gsap.quickTo(this.DOM.halo, 'x', { duration: this.opts.damp, ease: this.opts.ease });
    this._qToY = gsap.quickTo(this.DOM.halo, 'y', { duration: this.opts.damp, ease: this.opts.ease });
  }

  initEvents() {
    if (!this._enabled) return;
    this._recalcRect();

    const appear = () => gsap.to(this.DOM.halo, { autoAlpha: this.opts.opacity, duration: 0.35, ease: "power2.out" });
    const disappear = () => gsap.to(this.DOM.halo, { autoAlpha: 0, duration: 0.35, ease: "power2.out" });

    let _visible = false;

    const getXY = (e) => {
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0;
      const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY) ?? 0;
      if (this._useViewport) return { x: clientX + this.opts.offX, y: clientY + this.opts.offY };
      this._recalcRect();
      return { x: clientX - this._rect.left + this.opts.offX, y: clientY - this._rect.top + this.opts.offY };
    };

    const onOver = (e) => {
      const { x, y } = getXY(e);
      gsap.set(this.DOM.halo, { x, y });
      gsap.fromTo(this.DOM.inner, { scale: 0.98 }, { scale: 1.05, duration: 0.2, ease: "power2.out" });
      appear(); _visible = true;
    };

    const onOut = () => {
      gsap.to(this.DOM.inner, { scale: 1, duration: 0.2, ease: "power2.out" });
      disappear(); _visible = false;
    };

    const onMove = (e) => {
      const { x, y } = getXY(e);
      this._qToX(x); this._qToY(y);
      if (!_visible) { appear(); _visible = true; }
    };

    const TGT1 = this.$area;
    const TGT2 = this.$el;

    TGT1.addEventListener("pointerover", onOver);
    TGT1.addEventListener("pointerout", onOut);
    TGT1.addEventListener("pointermove", onMove);

    if (TGT2 !== TGT1) {
      TGT2.addEventListener("pointerover", onOver);
      TGT2.addEventListener("pointerout", onOut);
      TGT2.addEventListener("pointermove", onMove);
    }

    if (!this._useViewport) {
      window.addEventListener("resize", this._recalcRect);
      window.addEventListener("scroll", this._recalcRect, true);
    }

    this._handlers = { onOver, onOut, onMove, TGT1, TGT2 };
  }


  destroy() {
    const h = this._handlers || {};
    h.TGT1?.removeEventListener('pointerover', h.onOver);
    h.TGT1?.removeEventListener('pointerout', h.onOut);
    h.TGT1?.removeEventListener('pointermove', h.onMove);
    if (h.TGT2 && h.TGT2 !== h.TGT1) {
      h.TGT2.removeEventListener('pointerover', h.onOver);
      h.TGT2.removeEventListener('pointerout', h.onOut);
      h.TGT2.removeEventListener('pointermove', h.onMove);
    }

    if (!this._useViewport) {
      window.removeEventListener('resize', this._recalcRect);
      window.removeEventListener('scroll', this._recalcRect, true);
    }

    this.DOM.halo?.remove();
    this._handlers = null;
  }
}
